import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react';
import clsx from 'clsx';

type Align = 'left' | 'right' | 'center';

const alignClass: Record<Align, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

/**
 * The dominant table treatment in the app, extracted so new tables have one
 * target instead of re-deriving `w-full text-body-sm` + an uppercase head.
 *
 * `minWidth` puts the table in its own horizontal scroller rather than letting a
 * wide table push the page sideways.
 */
export function Table({
  children,
  minWidth,
  colWidths,
  className,
}: {
  children: ReactNode;
  minWidth?: number;
  /**
   * Fixed column widths (CSS lengths, e.g. ['auto', '8rem', '7rem']). Switches the
   * table to a fixed layout so columns stay put when data, period or filters change.
   */
  colWidths?: string[];
  className?: string;
}) {
  const table = (
    <table
      className={clsx('w-full text-body-sm', colWidths && 'table-fixed', className)}
      style={minWidth ? { minWidth } : undefined}
    >
      {colWidths && (
        <colgroup>
          {colWidths.map((w, i) => (
            <col key={i} style={w === 'auto' ? undefined : { width: w }} />
          ))}
        </colgroup>
      )}
      {children}
    </table>
  );
  return minWidth ? <div className="overflow-x-auto">{table}</div> : table;
}

/** Table head. `sticky` pins it while the body scrolls. */
export function THead({ children, sticky = false }: { children: ReactNode; sticky?: boolean }) {
  return (
    <thead
      className={clsx(
        'font-label text-eyebrow uppercase text-text-tertiary border-b border-border-light',
        sticky && 'sticky top-0 bg-surface-primary z-10'
      )}
    >
      {children}
    </thead>
  );
}

export function Th({
  children,
  align = 'left',
  className,
  ...rest
}: { children?: ReactNode; align?: Align } & ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th className={clsx('py-2.5 pr-4 last:pr-0 font-bold whitespace-nowrap', alignClass[align], className)} {...rest}>
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'left',
  className,
  ...rest
}: { children?: ReactNode; align?: Align } & TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={clsx('py-2.5 pr-4 last:pr-0', alignClass[align], className)} {...rest}>
      {children}
    </td>
  );
}

/** A body row with the standard hairline separator. */
export function Tr({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr
      className={clsx(
        'border-b border-border-light last:border-b-0',
        onClick && 'cursor-pointer hover:bg-surface-secondary transition-colors',
        className
      )}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}

/**
 * A group header inside a table body: the group's name on the left and a one-line
 * summary of the group beside it ("Paid ads · €13.4k cost · 0 won"). Rows that
 * belong to the group follow it.
 */
export function TGroupRow({
  label,
  summary,
  colSpan,
  icon,
}: {
  label: ReactNode;
  summary?: ReactNode;
  colSpan: number;
  icon?: ReactNode;
}) {
  return (
    <tr className="bg-surface-secondary border-b border-border-light">
      <td colSpan={colSpan} className="py-2 px-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span className="flex items-center gap-2 text-body-sm font-semibold text-text-primary">
            {icon}
            {label}
          </span>
          {summary && <span className="text-body-xs text-text-tertiary">{summary}</span>}
        </div>
      </td>
    </tr>
  );
}

/** The totals row: a heavier top rule and medium weight. Put it in a <tfoot>. */
export function TTotalRow({ children }: { children: ReactNode }) {
  return <tr className="border-t border-border font-medium text-text-primary">{children}</tr>;
}

/** A quiet line under a table summarising it — "27 customers · €80.4k MRR". */
export function TableFooter({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mt-3 flex items-center justify-between gap-4 text-body-xs text-text-tertiary">
      <div>{children}</div>
      {action}
    </div>
  );
}

/**
 * True when every row has the same value for a column — the signal to drop the
 * column and say the value once in a header instead.
 */
export function isConstant<T>(rows: readonly T[], pick: (row: T) => unknown): boolean {
  if (rows.length < 2) return false;
  const first = JSON.stringify(pick(rows[0]!));
  return rows.every((r) => JSON.stringify(pick(r)) === first);
}

/** An empty cell: a faint dot instead of a dash, so blanks recede. */
export function Nil() {
  return (
    <span className="text-text-muted" aria-label="none">
      ·
    </span>
  );
}

/**
 * A thin ink bar for a 0–1 share or confidence, the remainder dashed — reads at a
 * glance without a second colour.
 */
export function MeterBar({ value, className }: { value: number; className?: string }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <span className={clsx('relative inline-flex h-1 w-16 items-center', className)} aria-hidden>
      <span className="absolute inset-x-0 top-1/2 border-t border-dashed border-border" />
      <span className="relative h-1 rounded-full bg-text-primary" style={{ width: `${pct}%` }} />
    </span>
  );
}
